import React, { lazy } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Check, MessageCircle, Calendar, ArrowRight, Star, Shield, CreditCard, MapPin, Smartphone, HeartHandshake } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const MAPS_URL = 'https://maps.app.goo.gl/Fhof2j1jnLA3ffqT6';
const MAPS_EMBED_URL = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1241.0181846982155!2d-0.104187760804084!3d51.53089276153758!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x48760f7c6534e077%3A0xa54a590bd3b2a149!2sDentico%20Clinic%20-%20Private%20Dentist%20in%20Angel%2C%20Islington!5e0!3m2!1suk!2suk!4v1769722166469!5m2!1suk!2suk';
export function HomePage() {
  const {
    t,
    lang
  } = useTranslation();
  const showTrustBadge = lang === 'ua' || lang === 'ru';
  const features = [{
    icon: showTrustBadge ? HeartHandshake : Star,
    text: t('features.nativeLanguage')
  }, {
    icon: Smartphone,
    text: t('features.modernDentistry')
  }, {
    icon: Shield,
    text: t('features.clearPlans')
  }, {
    icon: CreditCard,
    text: t('features.honestPricing')
  }, {
    icon: Star,
    text: t('features.excellentReviews')
  }, {
    icon: MapPin,
    text: t('features.convenientLocation')
  }];
  const services = [{
    id: 'fillings',
    title: t('services.fillings.title'),
    desc: t('services.fillings.desc')
  }, {
    id: 'root-canal',
    title: t('services.rootCanal.title'),
    desc: t('services.rootCanal.desc')
  }, {
    id: 'crowns',
    title: t('services.crowns.title'),
    desc: t('services.crowns.desc')
  }, {
    id: 'hygiene',
    title: t('services.hygiene.title'),
    desc: t('services.hygiene.desc')
  }, {
    id: 'whitening',
    title: t('services.whitening.title'),
    desc: t('services.whitening.desc')
  }];
  const badgeColors = {
    en: {
      bg: 'bg-teal-100',
      text: 'text-teal-800',
      dot: 'bg-teal-500'
    },
    ua: {
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border border-blue-100',
      dot: 'bg-blue-500'
    },
    ru: {
      bg: 'bg-indigo-50',
      text: 'text-indigo-700',
      border: 'border border-indigo-100',
      dot: 'bg-indigo-500'
    }
  };
  const colors = badgeColors[lang];
  return <Layout data-id="element-221">
      {/* Hero Section */}
      <section className="relative bg-white overflow-hidden" data-id="element-222">
        <div className="absolute inset-0 bg-teal-50/50 -skew-y-3 origin-top-left transform translate-y-20" data-id="element-223" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 relative" data-id="element-224">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-225">
            <div className="space-y-8" data-id="element-226">
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${colors.bg} ${colors.text} ${colors.border || ''} text-sm font-medium`} data-id="element-227">
                <span className="relative flex h-2 w-2" data-id="element-228">
                  {lang === 'en' && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" data-id="element-229"></span>}
                  <span className={`relative inline-flex rounded-full h-2 w-2 ${colors.dot}`} data-id="element-230"></span>
                </span>
                {t('hero.badge')}
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight tracking-tight" data-id="element-231">
                {t('hero.title')}{' '}
                <span className="text-teal-600" data-id="element-232">
                  {t('hero.titleHighlight')}
                </span>
              </h1>

              <p className="text-xl text-gray-600 leading-relaxed max-w-lg" data-id="element-233">
                {t('hero.description')}
              </p>

              <div className="flex flex-col sm:flex-row gap-4" data-id="element-234">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-235">
                  <Button size="lg" fullWidth className="gap-2 shadow-lg shadow-teal-600/20" data-id="element-236">
                    <Calendar className="w-5 h-5" data-id="element-237" />
                    {t('nav.bookOnline')}
                  </Button>
                </a>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-238">
                  <Button variant="outline" size="lg" fullWidth className="gap-2" data-id="element-239">
                    <MessageCircle className="w-5 h-5 text-green-600" data-id="element-240" />
                    {t('hero.whatsapp')}
                  </Button>
                </a>
              </div>

              <div className="pt-8 border-t border-gray-100" data-id="element-241">
                <div className="grid grid-cols-2 gap-4" data-id="element-242">
                  {features.map((feature, idx) => <div key={idx} className="flex items-center gap-3 text-gray-700" data-id="element-243">
                      <div className="p-1.5 rounded-full bg-teal-50 text-teal-600" data-id="element-244">
                        <feature.icon className="w-4 h-4" data-id="element-245" />
                      </div>
                      <span className="text-sm font-medium" data-id="element-246">
                        {feature.text}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>

            <div className="relative hidden lg:block" data-id="element-247">
              <div className="absolute inset-0 bg-teal-200 rounded-3xl transform rotate-3 blur-sm opacity-30" data-id="element-248"></div>
              <img src="https://images.pexels.com/photos/3845810/pexels-photo-3845810.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="Modern Dental Clinic" className="relative rounded-3xl shadow-2xl object-cover h-[600px] w-full" data-id="element-249" />
              {showTrustBadge && <div className="absolute bottom-8 left-8 bg-white/95 backdrop-blur p-4 rounded-xl shadow-lg max-w-xs" data-id="element-250">
                  <div className="flex items-center gap-3 mb-2" data-id="element-251">
                    <div className={`w-8 h-8 rounded-full ${lang === 'ua' ? 'bg-blue-500' : 'bg-indigo-500'} flex items-center justify-center text-white text-xs font-bold border-2 border-white`} data-id="element-252">
                      {lang.toUpperCase()}
                    </div>
                    <span className="font-bold text-gray-900" data-id="element-253">
                      {t('trustBadge.title')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600" data-id="element-254">
                    {t('trustBadge.description')}
                  </p>
                </div>}
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 bg-gray-50" data-id="element-255">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-256">
          <div className="text-center max-w-2xl mx-auto mb-16" data-id="element-257">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-258">
              {t('services.title')}
            </h2>
            <p className="text-gray-600" data-id="element-259">{t('services.subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-260">
            {services.map((service, idx) => <Card key={idx} hover className="p-8 flex flex-col h-full" data-id="element-261">
                <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center text-teal-600 mb-6" data-id="element-262">
                  <Check className="w-6 h-6" data-id="element-263" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-264">
                  {service.title}
                </h3>
                <p className="text-gray-600 mb-6 flex-grow" data-id="element-265">{service.desc}</p>
                <Link to={`/${lang}/services/${service.id}`} className="inline-flex items-center text-teal-600 font-medium hover:text-teal-700" data-id="element-266">
                  {t('services.learnMore')}{' '}
                  <ArrowRight className="w-4 h-4 ml-1" data-id="element-267" />
                </Link>
              </Card>)}
          </div>

          <div className="mt-12 text-center" data-id="element-268">
            <Link to={`/${lang}/services`} data-id="element-269">
              <Button variant="secondary" size="lg" data-id="element-270">
                {t('services.viewAll')}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Location CTA */}
      <section className="py-24 bg-white" data-id="element-271">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-272">
          <div className="bg-teal-900 rounded-3xl overflow-hidden shadow-2xl" data-id="element-273">
            <div className="grid md:grid-cols-2" data-id="element-274">
              <div className="p-12 md:p-16 flex flex-col justify-center" data-id="element-275">
                <h2 className="text-3xl font-bold text-white mb-6" data-id="element-276">
                  {t('location.title')}
                </h2>
                <p className="text-teal-100 mb-8 text-lg" data-id="element-277">
                  {t('location.description')}
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-278">
                  <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" data-id="element-279">
                    <Button size="lg" data-id="element-280">{t('location.getDirections')}</Button>
                  </a>
                  <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" data-id="element-281">
                    <Button size="lg" data-id="element-282">{t('nav.bookOnline')}</Button>
                  </a>
                </div>
              </div>
              <div className="h-64 md:h-auto bg-teal-800 relative min-h-[300px]" data-id="element-283">
                <iframe src={MAPS_EMBED_URL} width="100%" height="100%" style={{
                border: 0
              }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Dentico Clinic Location" className="absolute inset-0" data-id="element-284"></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>;
}